/**
 * Helpers that translate between the carrier-facing Material datepicker
 * (which speaks in local-time `Date` objects) and the `YYYY-MM-DD` strings
 * the PFP backend expects on every weekStart query param.
 *
 * We deliberately stay in local time on both sides so the day the user
 * picked on the calendar is the same day we send to the API – no UTC-vs
 * timezone drift that would silently shift a Monday to the previous Sunday.
 */

/**
 * Parses a `YYYY-MM-DD` string into a `Date` anchored at local midnight.
 * Returns `null` for empty / malformed input so callers can patch the form
 * without poisoning the picker.
 */
export function parseISODateLocal(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const parts = iso.split('-');
  if (parts.length !== 3) {
    return null;
  }
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

/**
 * Formats a `Date` into `YYYY-MM-DD` using its local components, so the
 * string we send back to the API matches the day shown in the picker.
 */
export function formatLocalDate(d: Date | null | undefined): string | null {
  if (!d) {
    return null;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * PFP weeks always start on Monday. Wire this into `[matDatepickerFilter]`
 * to grey-out every other weekday and prevent invalid weekStart values
 * from ever reaching the backend.
 */
export const mondayDateFilter = (d: Date | null): boolean => {
  if (!d) {
    return false;
  }
  return d.getDay() === 1;
};
